import { useEffect } from "react";
import {
  FLAG_PENDING_ATTR,
  GE_FLAG_MEMORY_KEY,
} from "@/libs/locale/locale-ready";
import { isFixedPriceCountry } from "@/libs/global-e/countries";
import { FeatureFlagName, useIsFeatureOn } from "@/queries/feature-flag-query";
import { Env } from "@/Env";
import { GLOBALE_SCRIPT_ID, isGeMarkedCountry } from "@/libs/global-e/marker";
import {
  registerGeCheckoutCartClear,
  registerGeCheckoutFailureReport,
} from "@/libs/global-e/checkout-analytics";
import { registerGeWelcomeChangeCountry } from "@/libs/global-e/welcome-change-country";
import { buildGeLoaderSource } from "@/libs/global-e/script-loader";
import { useLocale } from "@/store/locale-store";
import { syncGlobalEDataCookie } from "@/libs/locale/global-e-cookie";

// Global-E storefront snippet (CSS + JS async loader).
// 정책: docs/global-e-storefront.md
// - 백엔드 feature flag `globalEEnabled === true` 일 때만 inject (§3.4 kill switch)
// - ★KR 에는 로드하지 않는다 (§1.1, §2.4 — 2026-10-12 되돌림). 「KR 에선 idle」 전제가 실측으로 깨졌다:
//   KR 에서도 매 로드 `localize?CountryCode=KR` 를 부르고, dev 에서 그 호출이 7만 회 넘게 반복돼 탭이 죽었다.
//   판정은 마커 단계와 **같은 술어**(`isGeMarkedCountry`)라 두 가드가 어긋날 수 없다
// - CSS는 <head> 최상단, JS는 인라인 loader(#globaleScript)를 </body> 직전에 — loader 가 엔진을 async 로 head append (§1.3)
// - ★`GlobalE_Data` 쿠키는 엔진을 넣기 **직전에** 여기서 쓴다(§3.5) — 엔진을 넣지 않는 곳(flag OFF · KR ·
//   webview)에선 쓰지 않는다. prod(flag OFF)가 `.bhaptics.com` 에 남긴 쿠키를 dev 엔진이 먼저 읽어 무한
//   재기록하다 탭이 멈췄다(2026-10-12).
// - SSR 안 함, hydration 후 client inject (§2.2 trade-off 수용)
// - 한 번 inject 후 flag를 off로 바꿔도 페이지 reload 전까지 제거되지 않음.
//   즉시 효과는 다음 reload부터 — kill switch는 신규 세션 기준.
const CSS_ID = "GEPIStyles";

/**
 * GE 가 가격을 처리하기 전 `[data-ge-price]` 를 숨기는 표시 — `src/styles/globals.css`.
 *
 * GE 국가 가격은 우리가 USD 로 먼저 그리고 GE 엔진이 현지 통화로 바꾼다. GE 는 처리 전 가격을 숨기고
 * 처리한 요소에 인라인 `visibility: visible` 을 넣는다(dev 실측 2026-10-12: AL·US·FR·GB 공통). 그
 * 숨김이 걸리기 전까지 `$ 529` 가 보였다 — 엔진을 넣는 순간 이 표시를 붙여 틈을 메운다.
 *
 * ★**푸는 시점은 GE CSS `load` 가 아니라 「GE 가 가격 하나를 처리했다」는 신호다.** 처음엔 `load` 에
 * 풀었는데(#5988), CSS 는 캐시에서 0.4초에 오고 GE 가 가격을 숨기는 건 엔진이 돌 때(1.4초 무렵)라
 * 그 사이 1초 동안 `$ 529` 가 다시 보였다(dev 실측 2026-10-12, KR → AL · 안도라 → AL · AL 직접 진입).
 * 숨기는 주체가 CSS 파일이 아니라 엔진이었다. 그래서 `[data-ge-price]` 에 인라인 `visibility: visible`
 * 이 붙는 첫 순간(= 엔진이 처리 중이고, 남은 가격은 엔진이 숨긴다)에 뗀다. 숨김 중에도 처리된 요소는
 * 인라인 스타일이 이겨 보인다. CSS 로드 실패면 바로, 신호가 안 오면 `GE_PENDING_TIMEOUT_MS` 뒤 뗀다.
 */
export const GE_PENDING_ATTR = "data-ge-pending";

/**
 * 고정가 국가(미국·유로존·영국)면 `<html data-ge-fixed>` — GE 엔진의 가격 숨김을 **무시하고 계속 보인다**.
 *
 * 고정가는 우리가 그린 숫자가 최종값이다(GE 는 숫자를 바꾸지 않고 미국만 표기를 `$ 565` → `$565.00` 로
 * 바꾼다). 그런데 엔진은 고정가 요소에도 숨김 → 인라인 visible 을 걸어 `€ 579 → 빈칸(0.3초) → € 579` 로
 * 깜빡였다(dev 실측 2026-10-12, FR·GB·US 공통: 2.0s 숨김 → 2.3s 보임). 처리 대기(`data-ge-pending`)를
 * 걸면 그 대신 1.4초 빈칸이 생겼다(#5993 이전). 둘 다 없애려고 고정가에선 숨김 자체를 덮는다(#5993 R1).
 */
export const GE_FIXED_ATTR = "data-ge-fixed";
export const GE_PENDING_TIMEOUT_MS = 5000;

export function GlobalEScripts() {
  const enabled = useIsFeatureOn(FeatureFlagName.GlobalEEnabled);
  const { countryCode, languageCode, isCountryHydrated } = useLocale();

  useEffect(() => {
    console.log("[GlobalE] enabled =", enabled);

    if (enabled !== true) {
      console.log("[GlobalE] skip: flag not on (enabled =", enabled, ")");
      return;
    }

    // 쿠키 국가가 확정되기 전엔 판단하지 않는다 — 그 전 `countryCode` 는 URL 로케일 파생 임시값
    // (`/en` → US)이라, 쿠키가 KR 인 사용자에게도 한 번 주입돼 버린다(`useLocale` 의 `isCountryHydrated` 주석).
    if (!isCountryHydrated) {
      console.log("[GlobalE] skip: country not hydrated yet");
      return;
    }

    // KR 은 GE 를 쓰지 않는다(자체 PG) — 엔진을 아예 넣지 않는다. KR → GE 국가로 바꾸면 `countryCode`
    // 가 바뀌어(`setLocale` 의 이동 도중) 이 effect 가 다시 돌고 그때 **처음** 주입된다. 아래에서 넣기 직전에
    // `GlobalE_Data` 를 그 국가로 쓰므로 엔진은 첫 로드에서 그 국가를 읽는다. GE 국가 → KR 은 `setLocale` 이 full reload
    // 하므로(엔진이 있었으니 게이트 통과) 새 페이지에서 여기서 걸러진다.
    // 고정가 국가 표시 — 국가가 바뀔 때마다 맞춘다(KR → GE 국가는 reload 없이 이 effect 가 다시 돈다)
    document.documentElement.toggleAttribute(
      GE_FIXED_ATTR,
      isGeMarkedCountry(countryCode) && isFixedPriceCountry(countryCode),
    );

    if (!isGeMarkedCountry(countryCode)) {
      console.log("[GlobalE] skip: not a GE country (", countryCode, ")");
      return;
    }

    // 앱 임베드 surface(/webview, /webview-pcp 등)는 웹 결제 흐름이 없고(구매는
    // 네이티브 앱이 처리), Global-E 통화변환/배송국가 팝업이 앱 컨텍스트에 부적절 →
    // 스니펫 미로드. locale prefix(/en/webview 등)와 webview-pcp 까지 잡도록 세그먼트
    // prefix 로 검사. (consent 배너의 isInIframe 가드와 동일 결.)
    // 정책/트레이드오프(prefix vs exact): docs/global-e-storefront.md §2.5
    if (
      window.location.pathname
        .split("/")
        .some((seg) => seg.startsWith("webview"))
    ) {
      console.log("[GlobalE] skip: webview path");
      return;
    }

    const { MerchantId, Host } = Env.GlobalE;
    console.log("[GlobalE] env =", { MerchantId, Host });
    if (!MerchantId || !Host) {
      console.log("[GlobalE] skip: missing MerchantId or Host");
      return;
    }

    if (!document.getElementById(CSS_ID)) {
      const link = document.createElement("link");
      link.id = CSS_ID;
      link.rel = "stylesheet";
      link.href = `https://${Host}/includes/css/${MerchantId}`;
      const root = document.documentElement;
      // 고정가(미국·유로존·영국)는 GE 가 숫자를 바꾸지 않는다 — 처리를 기다리면 빈칸만 길어진다
      // (dev 실측 2026-10-12: 프랑스 € 579 가 1.4초 숨었다). 숨김은 GE 가 바꾸는 Dynamic 만.
      if (!isFixedPriceCountry(countryCode))
        root.setAttribute(GE_PENDING_ATTR, "");
      // GE 가 처리한 가격 = 인라인 `visibility: visible` 이 붙은 `[data-ge-price]`
      const processed = (el: Element) =>
        el.hasAttribute("data-ge-price") &&
        (el as HTMLElement).style.visibility === "visible";
      const observer = new MutationObserver((records) => {
        if (records.some((r) => processed(r.target as Element))) release();
      });
      const timer = window.setTimeout(() => release(), GE_PENDING_TIMEOUT_MS);
      function release() {
        observer.disconnect();
        window.clearTimeout(timer);
        root.removeAttribute(GE_PENDING_ATTR);
      }
      observer.observe(document.body, {
        subtree: true,
        attributes: true,
        attributeFilter: ["style"],
      });
      // `load` 에선 풀지 않는다 — 위 docblock(CSS 는 일찍 오고 숨김은 엔진이 건다)
      link.onload = () => console.log("[GlobalE] CSS loaded:", link.href);
      link.onerror = (e) => {
        release();
        console.error("[GlobalE] CSS load error:", link.href, e);
      };
      document.head.prepend(link);
      console.log("[GlobalE] CSS injected:", link.href);
    } else {
      console.log("[GlobalE] CSS already present (#GEPIStyles)");
    }

    // id 는 엔진이 아니라 **인라인 로더**에 붙는다 — GE snippet 형태(#5837, script-loader.ts).
    // 로더는 §1.3 사양대로 `</body>` 직전(body 끝)에 둔다. 로더가 실행되며 엔진 `<script>`(id 없음)를
    // head 에 붙이고, 로드 로그도 로더 안에서 찍는다.
    if (!document.getElementById(GLOBALE_SCRIPT_ID)) {
      // 엔진이 읽을 `GlobalE_Data` 를 **넣기 직전에, 국가가 같아도 항상** 다시 쓴다. 헬퍼가 모든 스코프의
      // 같은 이름 쿠키를 먼저 지운다(`deleteCookieAllScopes`).
      //
      // 왜 항상인가 — 엔진은 `document.cookie` 의 **첫** `GlobalE_Data` 만 읽고, 쓰기는 merchant 설정의
      // `CookieDomain`(dev = `.dev.bhaptics.com`)에 한다. 상위 `.bhaptics.com` 에 prod 가 남긴 쿠키(flag OFF
      // 라 통화가 GE 판정과 다르다 — KR=KRW, FR=USD)가 있으면 엔진은 그걸 읽고 → 통화가 다르니 다시 쓰고
      // → 또 그걸 읽어 끝나지 않는다(dev 실측 2026-10-12: 탭 멈춤, 콜스택 `HandleUserLocalization →
      // SetLocalize → SetLocalizeCallback → Cookie.Get`). 국가만 비교해 건너뛰면 그 쿠키가 남는다.
      syncGlobalEDataCookie({ countryCode, languageCode });

      const src = `https://${Host}/includes/js/${MerchantId}`;
      const loader = document.createElement("script");
      loader.id = GLOBALE_SCRIPT_ID;
      loader.type = "text/javascript";
      loader.text = buildGeLoaderSource(src);
      // 엔진이 **읽고 뜬** 값을 로더에 남긴다 — 바로 위에서 쓴 쿠키와 같은 값이다. `setLocale` 이 이동 중에
      // 엔진이 들어왔을 때 이 값이 고른 국가·언어와 같으면 reload 하지 않는다(KR → GE 국가에서 reload 직후
      // 국가 표시가 URL 로케일 폴백(US)으로 깜빡이던 것, 2026-10-12). 쿠키 원문으로 비교하지 않는 이유:
      // 엔진이 로드 뒤 culture 를 자기 형식으로 다시 써서(`en-AL` → `en`) 늘 달라 보인다.
      loader.dataset.geCountry = countryCode.toUpperCase();
      loader.dataset.geLanguage = languageCode;
      document.body.appendChild(loader);
      console.log("[GlobalE] JS loader injected:", src);
    } else {
      console.log("[GlobalE] JS already present (#globaleScript)");
    }

    // GE 국제 체크아웃(IFRAME)은 결제 성공해도 우리 주문완료 페이지([order-id], useMount(clearCart))를
    // 안 타므로 카트가 남는다. GE 의 OnCheckoutStepLoaded(CONFIRMATION) 콜백에서 카트를 비운다.
    // docs/global-e-storefront.md §4.7. (모듈 싱글턴 가드로 중복 등록 무해.)
    registerGeCheckoutCartClear();

    // GE 결제창 안의 실패(CHECKOUT_FAILURE)를 서버 로그로 남긴다(#5807). (싱글턴 가드 — 중복 등록 무해.)
    registerGeCheckoutFailureReport();

    // GE 웰컴 팝업의 「change shipping country」 → GE 팝업을 닫고 우리 국가 스위처를 연다(#5843).
    // 우리는 GE 스위처 UI 를 안 쓰므로 이게 없으면 링크를 눌러도 아무 일도 안 일어난다.
    registerGeWelcomeChangeCountry();
  }, [enabled, isCountryHydrated, countryCode, languageCode]);

  // flag 를 기억한다 — 다음 첫 화면에서 부트 스크립트가 읽는다(`src/libs/locale/locale-ready.ts`)
  useEffect(() => {
    if (enabled !== true && enabled !== false) return;
    try {
      localStorage.setItem(GE_FLAG_MEMORY_KEY, enabled ? "1" : "0");
    } catch {
      // 저장 불가(사파리 개인정보 모드 등) — 기억 없이 종전 동작
    }
  }, [enabled]);

  // flag 와 국가가 확정되면 첫 화면의 flag 대기 숨김을 뗀다. ★위 주입 effect **뒤에** 선언 — 같은 커밋에서
  // 그 effect 가 Dynamic 국가의 `data-ge-pending` 을 먼저 붙이므로 숨김이 끊기지 않는다(effect 는 선언 순).
  // ★기다리는 건 **로딩 중(`null`)뿐**이다. 조회 실패·키 없음(`undefined` — `fetchFeatureFlags` 가 `[]`)이면 화면은
  // `=== true` 판정대로 flag OFF 화면이 최종이라 뗀다. 안 떼면 폴백(5초)까지 숨고, 기억도 안 남아 일본 첫 방문
  // 예외(`f === null`)가 **매 방문** 5초가 된다(#6007 R1 — flag 조회 장애 실측 08-31·09-02·09-03).
  useEffect(() => {
    if (enabled === null) return;
    if (!isCountryHydrated) return;
    document.documentElement.removeAttribute(FLAG_PENDING_ATTR);
  }, [enabled, isCountryHydrated]);

  return null;
}
